import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

const EXPLORE_LINKS = [
  { label: "Browse all cars", to: "/browse" },
  { label: "SUVs", to: "/browse?category=SUV" },
  { label: "Sedans", to: "/browse?category=Sedan" },
  { label: "Vans", to: "/browse?category=Van" },
];

const CITY_LINKS = [
  { label: "Car hire in Nairobi", to: "/browse?city=Nairobi" },
  { label: "Car hire in Mombasa", to: "/browse?city=Mombasa" },
  { label: "Car hire in Kisumu", to: "/browse?city=Kisumu" },
];

const COMPANY_LINKS = [
  { label: "How it works", to: "/#how" },
  { label: "Become a host", to: "/#host" },
  { label: "Rider reviews", to: "/#reviews" },
];

const POLICY_LINKS = [
  { label: "Terms of Service", to: "/policies/terms" },
  { label: "Cancellation policy", to: "/policies/cancellation" },
  { label: "Protection plans", to: "/policies/insurance" },
  { label: "Guest standards", to: "/policies/guest-standards" },
];

const FORM_ENDPOINT = "https://readdy.ai/api/form/dav3e3moc082o2i1jtk0";

export default function Footer() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string" && value.trim() !== "") {
        payload.append(key, value.trim());
      }
    });

    setStatus("loading");
    setFormError("");

    try {
      const response = await fetch(FORM_ENDPOINT, { method: "POST", body: payload });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMessage === "string" && serverMessage.toLowerCase().includes("spam");

      if (!response.ok || (parsed?.code && parsed.code !== "OK") || isSpam) {
        setStatus("error");
        setFormError(serverMessage || "We couldn't sign you up. Please try again.");
        return;
      }

      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("Network problem. Please check your connection and try again.");
    }
  }

  return (
    <footer className="bg-secondary-950 text-foreground-900">
      <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-8 md:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Link to="/" className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-lg text-background-50">
                <i className="ri-roadster-fill"></i>
              </span>
              <span className="font-heading text-xl font-semibold tracking-tight text-foreground-950">Gari</span>
            </Link>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-foreground-500">
              Kenya&apos;s peer-to-peer car rental marketplace. Book trusted cars from local hosts in Nairobi,
              Mombasa and beyond — or earn from the car sitting in your driveway.
            </p>
            <div className="mt-6 flex items-center gap-3">
              {[
                { icon: "ri-twitter-x-line", href: "https://twitter.com", label: "X" },
                { icon: "ri-instagram-line", href: "https://instagram.com", label: "Instagram" },
                { icon: "ri-facebook-circle-line", href: "https://facebook.com", label: "Facebook" },
                { icon: "ri-linkedin-line", href: "https://linkedin.com", label: "LinkedIn" },
              ].map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-background-50/10 text-lg text-foreground-800 transition-colors hover:bg-primary-500 hover:text-background-50"
                >
                  <i className={social.icon}></i>
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2">
            <h4 className="font-heading text-sm font-semibold uppercase tracking-wider text-foreground-800">Explore</h4>
            <ul className="mt-4 space-y-3">
              {EXPLORE_LINKS.map((link) => (
                <li key={link.label}>
                  <Link to={link.to} className="text-sm text-foreground-500 transition-colors hover:text-primary-400">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="font-heading text-sm font-semibold uppercase tracking-wider text-foreground-800">Cities</h4>
            <ul className="mt-4 space-y-3">
              {CITY_LINKS.map((link) => (
                <li key={link.label}>
                  <Link to={link.to} className="text-sm text-foreground-500 transition-colors hover:text-primary-400">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
            <h4 className="mt-6 font-heading text-sm font-semibold uppercase tracking-wider text-foreground-800">Gari</h4>
            <ul className="mt-4 space-y-3">
              {COMPANY_LINKS.map((link) => (
                <li key={link.label}>
                  <Link to={link.to} className="text-sm text-foreground-500 transition-colors hover:text-primary-400">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
            <h4 className="mt-6 font-heading text-sm font-semibold uppercase tracking-wider text-foreground-800">Policies</h4>
            <ul className="mt-4 space-y-3">
              {POLICY_LINKS.map((link) => (
                <li key={link.label}>
                  <Link to={link.to} className="text-sm text-foreground-500 transition-colors hover:text-primary-400">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="font-heading text-sm font-semibold uppercase tracking-wider text-foreground-800">
              Join the Gari list
            </h4>
            <p className="mt-4 text-sm leading-relaxed text-foreground-500">
              New cars, weekend deals and host tips — straight to your inbox.
            </p>
            <form
              id="gari-newsletter"
              data-readdy-form
              onSubmit={handleSubmit}
              className="mt-4 flex flex-col gap-3"
            >
              <div className="relative">
                <i className="ri-mail-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500"></i>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="you@email.com"
                  autoComplete="email"
                  className="w-full rounded-md border border-background-50/15 bg-background-50/5 py-3 pl-10 pr-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
                />
              </div>
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="field-extra"
              />
              <button
                type="submit"
                disabled={status === "loading"}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
              >
                {status === "loading" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i> Signing up
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i> Subscribe
                  </>
                )}
              </button>
              {status === "success" && (
                <p className="flex items-center gap-1.5 text-sm text-accent-400">
                  <i className="ri-checkbox-circle-line"></i> You&apos;re on the list. Karibu!
                </p>
              )}
              {status === "error" && (
                <p className="flex items-center gap-1.5 text-sm text-secondary-300">
                  <i className="ri-error-warning-line"></i> {formError}
                </p>
              )}
            </form>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-background-50/10 pt-6 sm:flex-row">
          <p className="text-xs text-foreground-500">
            © {new Date().getFullYear()} Gari Kenya. Rates in Kenyan Shillings. All rights reserved.
          </p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <span className="text-xs text-foreground-500">M-Pesa secured</span>
            <span className="text-xs text-foreground-500">Verified hosts</span>
            <span className="text-xs text-foreground-500">24/7 support</span>
          </div>
        </div>
      </div>
    </footer>
  );
}